import { DateTimeFormatter } from '@wix/bookings-date-time';
import type { ControllerFlowAPI, Experiments } from '@wix/yoshi-flow-editor';
import {
  PaymentMethod,
  PaymentOption,
  PaymentType,
  ReservedPaymentOptionIds,
  TFunction,
} from '../../types/types';
import {
  getDefaultPaymentOptionId,
  getPaymentTypeFromOption,
  getSlotServicePaymentOptions,
  isPlanAndBookBlockedForBooking,
  normalizeBuyPlanAndBookOption,
  sortPaymentOptions,
} from '../payment';
import { getDefaultPaymentType } from '../state/utils/utils';
import { getServiceSlotIdentifier } from '..';
import {
  FormState,
  Participant,
  SlotService,
  SlotServices,
} from '../state/types';
import { aggregateParticipantVariants } from '../mappers/aggregateParticipantVariants';
import { getParticipantVariants } from './participants';
import {
  getParticipantPayment,
  isOwnPaymentParticipant,
  isPlanPayment,
  withParticipantPayment,
} from './participantPayment';

export type PaymentChoicesEnv = {
  t: TFunction;
  settings: ControllerFlowAPI['settings'];
  experiments: Experiments;
  isPricingPlanInstalled: boolean;
  isPricingPlansMigratedToEcom?: boolean;
  isCart: boolean | undefined;
  dateRegionalSettingsLocale: string;
  dateAndTimeFormatter: DateTimeFormatter;
};

export const createPaymentChoicesEnv = ({
  dateAndTimeFormatter,
  ...env
}: Omit<PaymentChoicesEnv, 'dateAndTimeFormatter'> &
  Partial<
    Pick<PaymentChoicesEnv, 'dateAndTimeFormatter'>
  >): PaymentChoicesEnv => ({
  ...env,
  dateAndTimeFormatter:
    dateAndTimeFormatter ??
    new DateTimeFormatter(env.dateRegionalSettingsLocale),
});

export const toPaymentChoicesEnv = (
  {
    t,
    settings,
    experiments,
  }: Pick<PaymentChoicesEnv, 't' | 'settings' | 'experiments'>,
  {
    businessInfo,
    isPricingPlanInstalled,
    isPricingPlansMigratedToEcom,
    isCart,
  }: Pick<
    FormState,
    | 'businessInfo'
    | 'isPricingPlanInstalled'
    | 'isPricingPlansMigratedToEcom'
    | 'isCart'
  >,
): PaymentChoicesEnv =>
  createPaymentChoicesEnv({
    t,
    settings,
    experiments,
    isPricingPlanInstalled,
    isPricingPlansMigratedToEcom,
    isCart,
    dateRegionalSettingsLocale:
      businessInfo?.dateRegionalSettingsLocale ?? 'en-US',
  });

export type PaymentChoicesArgs = {
  participantIndex?: number;
  numberOfParticipants: number;
  excludePricingPlanOptions: boolean;
  isPlanAndBookBlocked: boolean;
  sortOrder: PaymentMethod;
};

export type PaymentChoices = {
  options: PaymentOption[];
  defaultOptionId?: string;
};

export const getOfferedPaymentOptions = (
  slotService: SlotService,
  {
    participantIndex,
    numberOfParticipants,
    excludePricingPlanOptions,
    isPlanAndBookBlocked,
    sortOrder,
  }: PaymentChoicesArgs,
  env: PaymentChoicesEnv,
): PaymentOption[] =>
  sortPaymentOptions(
    getSlotServicePaymentOptions({
      slotService,
      participantIndex,
      numberOfParticipants,
      excludePricingPlanOptions,
      isPlanAndBookBlocked,
      isPricingPlanInstalled: env.isPricingPlanInstalled,
      isPricingPlansMigratedToEcom: env.isPricingPlansMigratedToEcom,
      t: env.t,
      experiments: env.experiments,
      isCart: env.isCart,
      dateAndTimeFormatter: env.dateAndTimeFormatter,
      dateRegionalSettingsLocale: env.dateRegionalSettingsLocale,
    }),
    sortOrder,
  );

export const getPaymentChoices = (
  slotService: SlotService,
  args: PaymentChoicesArgs,
  env: PaymentChoicesEnv,
): PaymentChoices => {
  const {
    service,
    memberships,
    eligiblePlans,
    purchasableBenefits,
    dynamicPriceInfo,
  } = slotService;
  const { excludePricingPlanOptions, isPlanAndBookBlocked } = args;
  return {
    options: getOfferedPaymentOptions(slotService, args, env),
    defaultOptionId: getDefaultPaymentOptionId({
      servicePayment: service.payment,
      isPricingPlanInstalled: env.isPricingPlanInstalled,
      settings: env.settings,
      memberships,
      isDynamicPricingCustomOptions: !!dynamicPriceInfo?.customOptions,
      hasEligiblePlans: Boolean(
        eligiblePlans?.length || purchasableBenefits?.length,
      ),
      experiments: env.experiments,
      paymentTypes: service.paymentTypes,
      purchasableBenefits,
      excludePricingPlanOptions,
      isPlanAndBookBlocked,
      isPricingPlansMigratedToEcom: env.isPricingPlansMigratedToEcom,
    }),
  };
};

export const getParticipantPaymentChoicesArgs = (
  slotServices: SlotServices,
  slotService: SlotService,
  participantIndex: number,
  settings: ControllerFlowAPI['settings'],
): PaymentChoicesArgs => ({
  participantIndex,
  numberOfParticipants: 1,
  excludePricingPlanOptions: true,
  isPlanAndBookBlocked: isPlanAndBookBlockedForBooking(slotServices),
  sortOrder: getDefaultPaymentType(settings, slotService.service.paymentTypes),
});

export const findSameOption = (
  options: PaymentOption[],
  option?: PaymentOption,
) =>
  option
    ? options.find(
        ({ id, label }) => id === option.id && label === option.label,
      )
    : undefined;

const getPurchasedPlan = (
  option: PaymentOption,
  experiments: Experiments,
): { planId?: string; pricingVariantId?: string } | undefined => {
  if (option.type === PaymentType.BUY_PLAN) {
    return {
      planId: option.planId ?? option.id,
      pricingVariantId: option.pricingVariantId,
    };
  }
  if (option.type === PaymentType.BUY_PLAN_AND_BOOK) {
    return normalizeBuyPlanAndBookOption(option, experiments);
  }
  return undefined;
};

export const findSamePlanOption = (
  options: PaymentOption[],
  experiments: Experiments,
  option?: PaymentOption,
) => {
  const plan = option && getPurchasedPlan(option, experiments);
  if (!plan?.planId) {
    return undefined;
  }
  return options.find((candidate) => {
    const candidatePlan = getPurchasedPlan(candidate, experiments);
    return (
      !candidate.disabled &&
      !!candidatePlan &&
      candidatePlan.planId === plan.planId &&
      candidatePlan.pricingVariantId === plan.pricingVariantId
    );
  });
};

export const pickDefaultPaymentOption = (
  slotService: SlotService,
  participantIndex: number | undefined,
  { options, defaultOptionId }: PaymentChoices,
): PaymentOption | undefined => {
  if (isOwnPaymentParticipant(participantIndex)) {
    const previous = getParticipantPayment(slotService, participantIndex - 1);
    const inherited = isPlanPayment(previous)
      ? undefined
      : findSameOption(options, previous.selectedPaymentOption);
    if (inherited) {
      return inherited;
    }
  }
  if (!defaultOptionId) {
    return undefined;
  }
  return defaultOptionId === ReservedPaymentOptionIds.BuyAPricingPlan
    ? options.find(({ type }) => type === PaymentType.BUY_PLAN)
    : options.find(({ id }) => id === defaultOptionId);
};

const getDefaultParticipantPaymentOption = (
  slotServices: SlotServices,
  slotService: SlotService,
  participantIndex: number,
  env: PaymentChoicesEnv,
): PaymentOption | undefined =>
  pickDefaultPaymentOption(
    slotService,
    participantIndex,
    getPaymentChoices(
      slotService,
      getParticipantPaymentChoicesArgs(
        slotServices,
        slotService,
        participantIndex,
        env.settings,
      ),
      env,
    ),
  );

export const withDefaultParticipantPayments = (
  slotServices: SlotServices,
  slotService: SlotService,
  participants: Participant[],
  env: PaymentChoicesEnv,
): Participant[] => {
  const slotIdentifier = getServiceSlotIdentifier(slotService.nestedSlot);
  return participants.reduce((result, participant, index) => {
    if (!isOwnPaymentParticipant(index) || participant.payment) {
      return result;
    }
    const updatedSlotService = { ...slotService, participants: result };
    const option = getDefaultParticipantPaymentOption(
      { ...slotServices, [slotIdentifier]: updatedSlotService },
      updatedSlotService,
      index,
      env,
    );
    return option
      ? withParticipantPayment(result, index, {
          selectedPaymentOption: option,
          selectedPaymentType: getPaymentTypeFromOption(option),
        })
      : result;
  }, participants);
};

export const getParticipantsUpdate = (
  slotServices: SlotServices,
  slotService: SlotService,
  participants: Participant[],
  env: PaymentChoicesEnv,
): Partial<SlotService> => {
  const participantsWithPayments = withDefaultParticipantPayments(
    slotServices,
    slotService,
    participants,
    env,
  );
  const { dynamicPriceInfo } = slotService;
  return {
    participants: participantsWithPayments,
    ...(dynamicPriceInfo?.customOptions?.length
      ? {
          dynamicPriceInfo: {
            ...dynamicPriceInfo,
            selectedVariants: aggregateParticipantVariants(
              getParticipantVariants(participantsWithPayments),
            ),
          },
        }
      : {}),
  };
};
